import { useEffect, useRef, useState } from "react";
import { IStudioSelectItemModel } from "../models/studioSelectItemModel";
import { endpointsMetadata } from "../routes/endpointsMetadata";
import Cookies from "js-cookie";
import { UserRole } from "../../enums";
import { useLocation, useMatch, useNavigate } from "react-router-dom";
import { routePaths } from "../routes/routePaths";
import { useApiQuery, useClearAllQueryCache } from "./useApiQuery";
import { useStudioAccessConfirmation } from "./useStudioAccessConfirmation";
import { clearStudioAccessConfirmation } from "../utilities/studioAccessUtility";

export const SELECTED_STUDIO_ID_NAME = "SelectedStudioId";

export const useSelectStudio = (
  currentUserRole?: UserRole,
  isAuthenticated?: boolean
) => {
  const [userStudios, setUserStudios] = useState<IStudioSelectItemModel[]>([]);
  const [shouldRedirect, setShouldRedirect] = useState(false);

  const [selectedStudioId, setSelectedStudioId] = useState(() => {
    return Number(Cookies.get(SELECTED_STUDIO_ID_NAME) ?? 0);
  });

  const navigate = useNavigate();
  const location = useLocation();
  const studioAddMatch = useMatch(routePaths.studioAdd);
  const { clearCache } = useClearAllQueryCache();
  const processedUrlRef = useRef(new Set<string>());

  const { data: userStudiosData, isError: hasStudiosLoadFailed } = useApiQuery<
    IStudioSelectItemModel[]
  >({
    queryKey: [endpointsMetadata.initialization.getUserStudios],
    endpoint: endpointsMetadata.initialization.getUserStudios,
    globalIsLoading: !selectedStudioId,
    retry: false,
  });

  const setSelectedStudioInStorage = (studioId: number) => {
    Cookies.set(SELECTED_STUDIO_ID_NAME, studioId.toString(), { expires: 120 });
    clearCache();
    setSelectedStudioId(studioId);
  };

  const deleteSelectedStudioInStorage = () => {
    Cookies.remove(SELECTED_STUDIO_ID_NAME);
  };

  const clearSelectedStudio = () => {
    deleteSelectedStudioInStorage();
    setSelectedStudioId(0);
  };

  // A global admin confirms (and the server logs) every opening of a real studio.
  const {
    requestStudioSwitch,
    pendingStudioAccess,
    confirmStudioAccess,
    confirmStudioAccessSilently,
    cancelStudioAccess,
    isRecordingStudioAccess,
    isStudioAccessCheckPending,
  } = useStudioAccessConfirmation({
    currentUserRole,
    userStudios,
    selectedStudioId,
    hasPendingUrlStudio: new URLSearchParams(location.search).has("selectedStudio"),
    hasStudiosLoadFailed,
    applyStudioSelection: setSelectedStudioInStorage,
    clearStudioSelection: clearSelectedStudio,
  });

  useEffect(() => {
    if (userStudiosData) {
      setUserStudios(userStudiosData);
      setShouldRedirect(userStudiosData.length === 0);
    }
  }, [userStudiosData]);

  // URL studio synchronization logic (one-time processing per URL)
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const urlSelectedStudio = searchParams.get("selectedStudio");
    const currentUrl = location.pathname + location.search;

    if (
      urlSelectedStudio &&
      userStudios.length > 0 &&
      !processedUrlRef.current.has(currentUrl)
    ) {
      processedUrlRef.current.add(currentUrl);

      const studioIdFromUrl = Number(urlSelectedStudio);

      // Check if the studio ID from URL is valid and exists in user's available studios
      if (!isNaN(studioIdFromUrl) && studioIdFromUrl > 0) {
        const studioExists = userStudios.find(
          (studio) => studio.id === studioIdFromUrl
        );

        if (studioExists) {
          // Only set the studio if it's different from the currently selected one
          if (selectedStudioId !== studioIdFromUrl) {
            requestStudioSwitch(studioIdFromUrl);
          }
        }
      }

      // Always remove the parameter from URL (even if studio doesn't exist or is the same)
      searchParams.delete("selectedStudio");
      const newUrl =
        location.pathname +
        (searchParams.toString() ? `?${searchParams.toString()}` : "");
      navigate(newUrl, { replace: true });
    }
  }, [
    location.pathname,
    location.search,
    userStudios,
    selectedStudioId,
    navigate,
  ]);

  useEffect(() => {
    if (!studioAddMatch && isAuthenticated && shouldRedirect) {
      if (currentUserRole === UserRole.GlobalAdmin) {
        navigate(routePaths.tenantAdd);
      } else {
        navigate(routePaths.noStudiosFoundPage);
      }
    }

    // Read from userStudios (not the raw response) so the access gate can see the studio.
    if (!selectedStudioId && userStudios.length === 1) {
      requestStudioSwitch(userStudios[0].id);
    }
  }, [userStudios, location.pathname, currentUserRole]);

  /**
   * Drops the selected studio and the loaded list. `keepStudioAccessConfirmation` keeps a
   * global admin's confirmation for this session, so re-picking the same studio right after
   * (as the studio wizard makes them do) is not logged as a second opening.
   */
  const resetStudios = (options?: { keepStudioAccessConfirmation?: boolean }) => {
    clearSelectedStudio();
    if (!options?.keepStudioAccessConfirmation) {
      clearStudioAccessConfirmation();
    }
    setUserStudios([]);
  };

  useEffect(() => {
    clearCache();
  }, [selectedStudioId]);

  return {
    userStudios,
    selectedStudioId,
    setSelectedStudioInStorage,
    requestStudioSwitch,
    pendingStudioAccess,
    confirmStudioAccess,
    confirmStudioAccessSilently,
    cancelStudioAccess,
    isRecordingStudioAccess,
    isStudioAccessCheckPending,
    resetStudios,
  };
};
